import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import type { WaterPeriod } from '@models/water-period';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CrmWaterPeriodsService } from '@services/crm-water-periods.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { finalize } from 'rxjs';
import { isSeasonPeriodRowValid, SeasonPeriodRow, SeasonPeriodTableComponent } from './season-period-table.component';

/** The "Periods" tab: a water's recurring, yearly-repeating default season periods. */
@Component({
  selector: 'app-season-periods-default-tab',
  templateUrl: './season-periods-default-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatProgressSpinnerModule, TranslatePipe, SeasonPeriodTableComponent],
})
export class SeasonPeriodsDefaultTabComponent implements OnInit {
  readonly waterId = input.required<number>();
  /** Emits the period count so the parent can show it as a tab badge. */
  readonly count = output<number>();

  private readonly periodsService = inject(CrmWaterPeriodsService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly rows = signal<SeasonPeriodRow[]>([]);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);

  /**
   * Every row must have a season selected before saving. Ranges may run backwards here: a recurring
   * period crossing the year end is a winter, entered as one row.
   */
  readonly isValid = computed(() => this.rows().every((row) => isSeasonPeriodRowValid(row, true)));
  /** Set when Save is clicked with invalid rows; cleared as soon as the rows are edited. */
  readonly showValidationError = signal(false);

  constructor() {
    effect(() => this.count.emit(this.rows().length));
  }

  onRowsChange(rows: SeasonPeriodRow[]): void {
    this.rows.set(rows);
    this.showValidationError.set(false);
  }

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.isLoading.set(true);
    this.periodsService
      .getPeriods(this.waterId())
      .pipe(
        showGenericErrorToast(
          this.toastService,
          () => [] as WaterPeriod[],
          this.translate.instant('waters.season-periods.load-failed'),
        ),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe((periods) => this.rows.set(periods.map(toRow)));
  }

  save(): void {
    if (!this.isValid()) {
      this.showValidationError.set(true);
      return;
    }
    this.isSaving.set(true);
    this.periodsService
      .savePeriods(this.waterId(), { periods: this.rows().map(toPeriod) })
      .pipe(finalize(() => this.isSaving.set(false)))
      .subscribe({
        next: (periods) => {
          this.rows.set(periods.map(toRow));
          this.toastService.showSuccess(this.translate.instant('waters.season-periods.save-success'));
        },
        error: () => this.toastService.showError(this.translate.instant('waters.season-periods.save-failed')),
      });
  }
}

function toRow(p: WaterPeriod): SeasonPeriodRow {
  return { ...p, clientId: crypto.randomUUID() };
}

function toPeriod(r: SeasonPeriodRow): WaterPeriod {
  return {
    id: r.id,
    periodTypeId: r.periodTypeId,
    fromWeek: r.fromWeek,
    fromDay: r.fromDay,
    untilWeek: r.untilWeek,
    untilDay: r.untilDay,
  };
}
